import { FC, ReactNode } from "react";
import { Link } from "@/types/General";
import ConsolidatedNavigation from "./Desktop/ConsolidatedNavigation";
import SuperHeader from "./Desktop/SuperHeader";
import Header from "./Desktop/Header";
import MobileNavigation from "./Mobile/MobileNavigation";
import { LocaleCode } from "@/types";
import cn from "classnames";

export type CategoryLinkItem = {
  categoryTitle?: string;
  categoryTitleMobileDLD?: string;
  categoryTitleMobileDLT?: string;
  categoryDescription?: ReactNode;
  linksColumnCount?: "1" | "2" | "3";
  icon?: ReactNode;
  cta?: ReactNode;
  links?: Link[];
};

export type GlobalNavigationMenuItem = {
  leftContent?: ReactNode;
  leftCta?: ReactNode;
  leftLinksTitle?: string;
  leftLinks?: Link[];
  categoryLinksInTwoColumns?: boolean;
  categoryLinks?: CategoryLinkItem[];
  bottomLinksTitle?: string;
  bottomLinks?: Link[];
} & Link;

export type SuperHeaderItem = {
  skipToMainContentLabel: string;
  items: {
    title: string;
    href?: string;
    dlt: string;
    dld: string;
    links?: Link[];
  }[];
  languageSwitcher?: {
    languageButtonLabel: string;
    languageButtonDLD: string;
    languageButtonDLT: string;
    languages: ({
      icon?: string;
      ariaLabel: string;
      languageCode: string;
    } & Link)[];
  };
  search: {
    showButtonLabel: string;
    showButtonDLD: string;
    showButtonDLT: string;
    hideButtonLabel: string;
    hideButtonDLD: string;
    hideButtonDLT: string;
    inputLabel: string;
    inputPlaceholder: string;
    submitButtonLabel: string;
    submitButtonDLD: string;
    submitButtonDLT: string;
  };
};

export type GlobalNavigationProps = {
  sticky?: boolean;
  superHeader: SuperHeaderItem;
  menu: {
    colorVariant: "coolGray" | "blue" | "gray";
    logo?: ReactNode;
    mobileLogoExpanded?: ReactNode;
    mobileHamburgerMenuCollapsedDLD?: string;
    mobileHamburgerMenuCollapsedDLT?: string;
    mobileHamburgerMenuExpandedDLD?: string;
    mobileHamburgerMenuExpandedDLT?: string;
    items?: GlobalNavigationMenuItem[];
    mobileBackLink?: {
      dlt?: string;
      dld?: string;
      backToText?: string;
      mainMenuText?: string;
    };
    cta?: ReactNode;
  };
  locale?: LocaleCode;
  asPath?: string;
  contactLink?: Link;
  loginMenu?: {
    ariaLabel?: string;
    dlt?: string;
    dld?: string;
    links?: Link[];
  };
  isConsolidatedNavigation?: boolean;
  isDocked?: boolean;
};

const GlobalNavigation: FC<GlobalNavigationProps> = ({
  sticky,
  superHeader,
  menu,
  locale = "en-us",
  asPath = "/",
  contactLink,
  loginMenu,
  isConsolidatedNavigation = false,
  isDocked = false,
}) => {
  return (
    <>
      <header
        className={cn(
          "relative z-40 hidden lg:block",
          sticky && "sticky top-0 z-1",
        )}
      >
        {isConsolidatedNavigation ? (
          <ConsolidatedNavigation
            sticky={sticky}
            superHeader={superHeader}
            menu={menu}
            locale={locale}
            asPath={asPath}
            contactLink={contactLink}
            loginMenu={loginMenu}
            isDocked={isDocked}
          />
        ) : (
          <>
            <SuperHeader
              superHeader={superHeader}
              locale={locale}
              asPath={asPath}
            />
            <Header menu={menu} />
          </>
        )}
      </header>
      <header
        className={cn("relative z-50 lg:hidden", sticky && "sticky top-0 z-1")}
      >
        <MobileNavigation
          sticky={sticky}
          superHeader={superHeader}
          menu={menu}
          asPath={asPath}
          locale={locale}
        />
      </header>
    </>
  );
};

export default GlobalNavigation;
